// calendar.js

export const formatDateYYYYMMDD = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateYYYYMM = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export const formatDate = date =>
  date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

export const newDateMidnight = dateString => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const remInPixels = rems => {
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return rems * rootFontSize;
};

export const scrollToElementWithOffset = (targetElement, offsetElement, remOffset = 1) => {
  if (!targetElement) return;

  let offset = 0;
  if (offsetElement && offsetElement.offsetHeight) {
    offset = offsetElement.offsetHeight;
  }

  if (remOffset) {
    offset += remInPixels(remOffset);
  }

  const rect = targetElement.getBoundingClientRect();
  const absoluteTop = rect.top + window.scrollY;
  const finalPosition = absoluteTop - offset;

  if (typeof window.scrollTo === 'function') {
    window.scrollTo({
      top: finalPosition,
      behavior: 'smooth'
    });
  }
};

export async function initCalendar() {
  let calendarConfig;
  try {
    const response = await fetch('/config/calendar.config.json');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    calendarConfig = await response.json();
  } catch (error) {
    console.error('Could not load calendar configuration:', error);
    return;
  }

  const calendarContainer = document.getElementById('calendar-container');
  const calendarKeyContainer = document.getElementById('calendar-key-container');
  if (!calendarContainer || !calendarKeyContainer) return;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const startYear = calendarConfig.startYear;
  const startMonth = calendarConfig.startMonth;
  const monthsToShow = calendarConfig.monthsToShow;
  const allPeriods = calendarConfig.events.map(event => ({
    ...event,
    startDate: newDateMidnight(event.startDate),
    endDate: newDateMidnight(event.endDate)
  }));

  const groupedPeriods = allPeriods.reduce((acc, period) => {
    if (!acc[period.name]) {
      acc[period.name] = [];
    }
    acc[period.name].push(period);
    return acc;
  }, {});

  const popup = document.createElement('div');
  popup.classList.add('popup');
  document.body.appendChild(popup);

  const overlay = document.createElement('div');
  overlay.classList.add('overlay');
  document.body.appendChild(overlay);

  function hidePopup() {
    popup.style.display = 'none';
    overlay.style.display = 'none';
  }

  function showPopup(date) {
    const currentPeriod = allPeriods.find(p => date >= p.startDate && date <= p.endDate);

    if (!currentPeriod) {
      hidePopup();
      return;
    }

    const periodName = currentPeriod.name;
    const periodClassName = currentPeriod.className;

    let periodsToShow;
    let headingText;

    if (periodClassName === 'holiday') {
      periodsToShow = allPeriods.filter(p => p.className === 'holiday');
      headingText = 'Holidays';
    } else {
      periodsToShow = groupedPeriods[periodName];
      headingText = periodName;
    }

    let totalDays = periodsToShow.reduce((sum, p) => {
      const oneDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round(Math.abs((p.endDate - p.startDate) / oneDay)) + 1;
      return sum + diffDays;
    }, 0);
    let weeks = Math.round(totalDays / 7);

    let content = `<h2>${headingText}`;

    if (weeks > 0 && periodClassName !== 'holiday') {
      let weekText = weeks === 1 ? 'week' : 'weeks';
      content += ` (${weeks} ${weekText})`;
    }
    content += `</h2>`;

    periodsToShow.forEach(p => {
      if (p.className === 'holiday') {
        content += `<p class="${p.className}">${p.name}<br>${formatDate(p.startDate)} - ${formatDate(p.endDate)}</p>`;
      } else {
        content += `<p class="${p.className}">${formatDate(p.startDate)} - ${formatDate(p.endDate)}</p>`;
      }
    });

    const holidayBreaks = allPeriods.filter(
      p => p.name.includes('holiday') || p.name.includes('break') || p.name.includes('Festive')
    );

    if (periodClassName !== 'holiday' && holidayBreaks.length > 0) {
      const breaksInPeriod = holidayBreaks.filter(h => {
        return periodsToShow.some(p => {
          return h.startDate > p.startDate && h.endDate < p.endDate;
        });
      });

      if (breaksInPeriod.length > 0) {
        content += '<h3>Breaks:</h3>';
        breaksInPeriod.forEach(b => {
          content += `<p>(${formatDate(b.startDate)} - ${formatDate(b.endDate)})</p>`;
        });
      }
    }

    content += `<button class="close-button">x</button>`;
    popup.innerHTML = content;
    popup.style.display = 'block';
    overlay.style.display = 'block';

    const closeBtn = popup.querySelector('.close-button');
    if (closeBtn) {
      closeBtn.addEventListener('click', hidePopup);
    }
  }

  overlay.addEventListener('click', hidePopup);

  // Key generation
  const keyContainer = document.createElement('div');
  const keyList = document.createElement('ul');

  const allUniquePeriods = [...new Map(allPeriods.map(item => [item.className, item])).values()];
  const termPeriods = allUniquePeriods.filter(period => period.className !== 'holiday');
  const holidayPeriod = allUniquePeriods.find(period => period.className === 'holiday');

// Add diagnostic log inside the click listener
termPeriods.forEach(period => {
  const keyItem = document.createElement('li');
  keyItem.classList.add(period.className);
  keyItem.textContent = period.name;

  keyItem.addEventListener('click', () => {
    const firstPeriod = allPeriods.find(p => p.className === period.className);
    if (!firstPeriod) {
      return;
    }
    const dataMonth = formatDateYYYYMM(firstPeriod.startDate);
    const targetMonthElement = document.querySelector(`[data-month="${dataMonth}"]`);
    if (targetMonthElement) {
      scrollToElementWithOffset(targetMonthElement, calendarKeyContainer);
    }
    showPopup(firstPeriod.startDate);
  });

  keyList.appendChild(keyItem);
});

  // Holiday period click listener restored
  if (holidayPeriod) {
    const holidayKeyItem = document.createElement('li');
    holidayKeyItem.classList.add('holiday');
    holidayKeyItem.textContent = 'Holidays';

    holidayKeyItem.addEventListener('click', () => {
      const firstHolidayPeriod = allPeriods.find(p => p.className === 'holiday');
      if (firstHolidayPeriod) {
        const dataMonth = formatDateYYYYMM(firstHolidayPeriod.startDate);
        const targetMonthElement = document.querySelector(`[data-month="${dataMonth}"]`);
        if (targetMonthElement) {
          scrollToElementWithOffset(targetMonthElement, calendarKeyContainer);
        }
        showPopup(firstHolidayPeriod.startDate);
      }
    });

    keyList.appendChild(holidayKeyItem);
  }

  keyContainer.appendChild(keyList);
  calendarKeyContainer.prepend(keyContainer);

  // Calendar rendering
  for (let i = 0; i < monthsToShow; i++) {
    let currentMonth = startMonth + i;
    let currentYear = startYear;

    if (currentMonth > 11) {
      currentMonth = currentMonth % 12;
      currentYear += Math.floor((startMonth + i) / 12);
    }

    const monthElement = document.createElement('div');
    monthElement.classList.add('month');
    const cdataMonth = formatDateYYYYMM(new Date(currentYear, currentMonth, 1));
    monthElement.setAttribute('data-month', cdataMonth);

    const monthName = document.createElement('div');
    monthName.classList.add('month-name');
    monthName.textContent = `${monthNames[currentMonth]} ${currentYear}`;
    monthElement.appendChild(monthName);

    const weekdays = document.createElement('div');
    weekdays.classList.add('weekdays');
    dayNames.forEach(day => {
      const dayEl = document.createElement('div');
      dayEl.textContent = day;
      weekdays.appendChild(dayEl);
    });
    monthElement.appendChild(weekdays);

    const daysGrid = document.createElement('div');
    daysGrid.classList.add('days');

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const firstDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    for (let j = 0; j < firstDayIndex; j++) {
      const emptyDay = document.createElement('div');
      emptyDay.classList.add('day', 'empty');
      daysGrid.appendChild(emptyDay);
    }

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const dayElement = document.createElement('div');
      dayElement.classList.add('day');
      dayElement.textContent = day;
      const fullDate = new Date(currentYear, currentMonth, day);
      dayElement.setAttribute('data-date', formatDateYYYYMMDD(fullDate));

      const dayOfWeek = fullDate.getDay();
      if (dayOfWeek === 6) dayElement.classList.add('saturday');
      if (dayOfWeek === 0) dayElement.classList.add('sunday');

      allPeriods.forEach(period => {
        if (fullDate >= period.startDate && fullDate <= period.endDate) {
          dayElement.classList.add(period.className);
        }
      });

      dayElement.addEventListener('click', () => showPopup(fullDate));
      daysGrid.appendChild(dayElement);
    }

    monthElement.appendChild(daysGrid);
    calendarContainer.appendChild(monthElement);
  }

  // Handle URL Hash scrolling
  const hash = window.location.hash;
  if (hash) {
    const targetMonthElement = document.querySelector(`[data-month="${hash.substring(1)}"]`);
    if (targetMonthElement) {
      scrollToElementWithOffset(targetMonthElement, calendarKeyContainer);
    }
  }

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && popup.style.display === 'block') {
      hidePopup();
    }
  });
}

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  document.addEventListener('DOMContentLoaded', initCalendar);
}
